import { inject } from '@angular/core';
import { RedirectFunction, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { readProfile } from '../../../core/auth/capability.guard';
import { BAND_STUDIO_VIEW, holdsAnyCapability } from '../../../core/auth/capabilities';
import { TenantLifecycleService } from '../../../core/tenant/tenant-lifecycle.service';
import { BandStudioApiService } from '../../../shared/services/api/band-studio-api.service';
import { UserApiService } from '../../../shared/services/api/user-api.service';
import { runsNewestFirst } from './band-studio-display';
import { runSummaryCommands, summaryCommands } from './band-studio-routes';

/**
 * The old address of a model's saved logic (models/:slug/v/:version). A model is its run
 * now, so a bookmark opens the newest run of that logic; a `?run=` on the old address named
 * the run itself. The customer and any other query ride along; the number does not.
 */
export const modelVersionRedirect: RedirectFunction = async ({ params, queryParams }) => {
  const router = inject(Router);
  const api = inject(BandStudioApiService);
  const slug = params['slug'] ?? '';
  const { run, ...rest } = queryParams;
  const to = (commands: string[]) => router.createUrlTree(commands, { queryParams: rest });
  if (typeof run === 'string' && run) return to(runSummaryCommands(slug, run));
  // the router runs no guard on a redirect, so the lookup keeps the Studio's gate itself: a
  // user the Studio does not admit goes to the model page, whose guards turn them away unread
  if (!(await admitted())) return to(summaryCommands(slug));
  const version = Number(params['version']);
  try {
    const { models } = await firstValueFrom(api.listModels());
    // a group can hold models of another slug, so the runs are read across every card
    const newest = runsNewestFirst(
      models
        .flatMap((m) => m.runs)
        .filter((r) => r.slug === slug && !r.draft && r.version === version)
    )[0];
    if (newest) return to(runSummaryCommands(slug, newest.run_id));
  } catch {
    // the model's own page says what is wrong with the list
  }
  return to(summaryCommands(slug));
};

/** The `band_studio.view` permission the Studio routes ask for, read once the tenant bootstrap has settled. */
async function admitted(): Promise<boolean> {
  const lifecycle = inject(TenantLifecycleService);
  const userApi = inject(UserApiService);
  try {
    await firstValueFrom(lifecycle.bootstrapTenantContext());
    const me = await readProfile(userApi);
    return holdsAnyCapability(me.permissions, [BAND_STUDIO_VIEW]);
  } catch {
    return false;
  }
}
